import type { InputActionBarConfig } from '../components/InputActionBar/InputActionBar.types';

/**
 * Returns whether the action bar should be shown.
 * The bar is visible when at least one of showUpload, showPlusMenu, showSend, or showCancel is true.
 */
export function hasActionBar(
  config: InputActionBarConfig | null | undefined
): boolean {
  return Boolean(
    config &&
      (config.showUpload ||
        config.showPlusMenu ||
        config.showSend ||
        config.showCancel)
  );
}

export interface GetActionBarConfigForEditOptions {
  submitOnBlur: boolean;
  onCancelClick: () => void;
  /** Optional overrides; when not hiding send/cancel, fallback is config then true */
  showSend?: boolean;
  showCancel?: boolean;
}

export interface GetActionBarConfigForEditResult {
  actionBarConfig: InputActionBarConfig | undefined;
  /** When true, render CommentSubmitButtons (inline Save/Cancel). When false, use action bar or submitOnBlur only. */
  showCommentSubmitButtons: boolean;
}

/**
 * Derives action bar config for edit/reply mode. All previous prop combinations remain supported:
 *
 * | submitOnBlur | showDefaultEditButtons | Result |
 * |--------------|------------------------|--------|
 * | true         | any                    | No send/cancel in bar, no inline buttons (submit on blur only). |
 * | false        | true                   | Use bar for send/cancel; showSend/showCancel default true, or use config/caller overrides. |
 * | false        | false/undefined        | No send/cancel in bar; show inline CommentSubmitButtons. |
 *
 * When config is undefined: returns a minimal synthetic config (onCancelClick, showSend/showCancel false)
 * so reply/edit consumers get a stable object and defaults are non-breaking.
 * When config is passed but showSend/showCancel are omitted: they default to true when the bar shows send/cancel.
 */
export function getActionBarConfigForEdit(
  config: InputActionBarConfig | undefined,
  options: GetActionBarConfigForEditOptions
): GetActionBarConfigForEditResult {
  if (!config) {
    return {
      actionBarConfig: {
        onCancelClick: options.onCancelClick,
        showSend: false,
        showCancel: false
      },
      showCommentSubmitButtons: !options.submitOnBlur
    };
  }

  const useActionBarForEdit =
    !options.submitOnBlur && config.showDefaultEditButtons === true;
  const hideSendCancelInBar = options.submitOnBlur || !useActionBarForEdit;

  const actionBarConfig: InputActionBarConfig = {
    ...config,
    onCancelClick: options.onCancelClick,
    ...(hideSendCancelInBar
      ? { showSend: false, showCancel: false }
      : {
          showSend: options.showSend ?? config.showSend ?? true,
          showCancel: options.showCancel ?? config.showCancel ?? true
        })
  };

  return {
    actionBarConfig,
    showCommentSubmitButtons: !options.submitOnBlur && !useActionBarForEdit
  };
}

/**
 * Wraps config's onCancelClick to run after a focus callback (e.g. refocus textarea).
 * Use in inputs that render the action bar and want to keep focus after cancel.
 */
export function withCancelClickFocus(
  config: InputActionBarConfig,
  focusAfter: () => void
): InputActionBarConfig {
  return {
    ...config,
    onCancelClick: () => {
      config.onCancelClick?.();
      focusAfter();
    }
  };
}
